import { create } from 'zustand';

import type { BlacklistedPlayer } from './blacklist-slice';
import { useAITransfersStore } from './useAITransfersStore';

interface RecommendedTransfersStore {
  disabledPlayerIds: string[];
  pendingPlayerIdsIn: string[];
  pendingBlacklistedInPlayers: BlacklistedPlayer[];
  activePlayerIdsIn: string;

  disablePlayer: (playerId: string) => void;
  restorePlayer: (playerId: string) => void;
  addPendingPlayerIn: (playerId: string) => void;
  addPendingBlacklistedIn: (entry: BlacklistedPlayer) => void;
  commitRegenerate: () => void;
  reset: () => void;
}

export const useRecommendedTransfersStore = create<RecommendedTransfersStore>(
  (set, get) => ({
    disabledPlayerIds: [],
    pendingPlayerIdsIn: [],
    pendingBlacklistedInPlayers: [],
    activePlayerIdsIn: '',

    disablePlayer: (playerId) =>
      set((state) => {
        if (state.disabledPlayerIds.includes(playerId)) return state;
        return { disabledPlayerIds: [...state.disabledPlayerIds, playerId] };
      }),

    restorePlayer: (playerId) =>
      set((state) => ({
        disabledPlayerIds: state.disabledPlayerIds.filter(
          (id) => id !== playerId
        ),
        pendingPlayerIdsIn: state.pendingPlayerIdsIn.filter(
          (id) => id !== playerId
        ),
        pendingBlacklistedInPlayers: state.pendingBlacklistedInPlayers.filter(
          (p) => p.id !== playerId
        )
      })),

    addPendingPlayerIn: (playerId) =>
      set((state) => {
        if (state.pendingPlayerIdsIn.includes(playerId)) return state;
        return {
          pendingPlayerIdsIn: [...state.pendingPlayerIdsIn, playerId]
        };
      }),

    addPendingBlacklistedIn: (entry) =>
      set((state) => {
        const exists = state.pendingBlacklistedInPlayers.some(
          (p) => p.id === entry.id && p.type === entry.type
        );
        if (exists) return state;
        return {
          pendingBlacklistedInPlayers: [
            ...state.pendingBlacklistedInPlayers,
            entry
          ]
        };
      }),

    commitRegenerate: () => {
      const {
        pendingBlacklistedInPlayers,
        pendingPlayerIdsIn,
        activePlayerIdsIn
      } = get();
      const { addBlacklistedPlayer } = useAITransfersStore.getState();

      pendingBlacklistedInPlayers.forEach((entry) => {
        addBlacklistedPlayer(entry);
      });

      const existingIds = activePlayerIdsIn ? activePlayerIdsIn.split(',') : [];
      const mergedPlayerIdsIn = [
        ...new Set([...existingIds, ...pendingPlayerIdsIn])
      ].join(',');

      set({
        activePlayerIdsIn: mergedPlayerIdsIn,
        disabledPlayerIds: [],
        pendingPlayerIdsIn: [],
        pendingBlacklistedInPlayers: []
      });
    },

    reset: () =>
      set({
        disabledPlayerIds: [],
        pendingPlayerIdsIn: [],
        pendingBlacklistedInPlayers: [],
        activePlayerIdsIn: ''
      })
  })
);
